"use client";
import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send } from 'lucide-react';
import CrescentMoon from './CrescentMoon';
import { COMPANY } from '../data/mock';
import { brandConfig } from '../brandConfig';

const CHIPS = ['Umrah Package', 'Flight Ticket', 'Visa Help', 'Get a Price'];

const OPENER = `Marhaba! 🌙 I'm your ${brandConfig.companyName} travel advisor. Whether you're planning Umrah, or need a flight or visa — I'm here to help. What journey are you planning?`;

const replies = {
  umrah: `Alhamdulillah! Our Umrah packages start at ETB 165,000 (Economy, 15 days). We offer Standard (ETB 115,000), VIP (ETB 195,000), and a new Couple Visa+Ticket package (ETB 230,000). Call ${COMPANY.phoneDisplay} to book. نتمنى لك رحلة مباركة`,
  flight: `We can book flights to Saudi Arabia, UAE, Turkey, and worldwide. Tickets to Jeddah start from ETB 35,000. Call ${COMPANY.phoneDisplay} for current rates, Inshallah.`,
  visa: `Alhamdulillah, our visa success rate is 100%. We handle Umrah visas (ETB 40,000), Saudi tourist, UAE and Schengen visas with full document support. Call ${COMPANY.phoneDisplay} to start your application.`,
  price: `Our packages: Visa Only ETB 40,000 · Standard ETB 115,000 · Economy Umrah ETB 165,000 · VIP ETB 195,000 · Couple Visa+Ticket ETB 230,000. Call ${COMPANY.phoneDisplay}, we are open 24/7. بارك الله فيك`,
  default: `Assalamu Alaikum! For complete details, please call us at ${COMPANY.phoneDisplay}. We are open 24 hours, Inshallah we can help with your sacred journey.`,
};

function getReply(text) {
  const t = text.toLowerCase();
  if (t.includes('umrah') || t.includes('umra') || t.includes('عمرة')) return replies.umrah;
  if (t.includes('flight') || t.includes('ticket') || t.includes('تذكرة')) return replies.flight;
  if (t.includes('visa') || t.includes('تأشيرة')) return replies.visa;
  if (t.includes('price') || t.includes('cost') || t.includes('quote')) return replies.price;
  return replies.default;
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ from: 'ai', text: OPENER }]);
  const [input, setInput] = useState('');
  const [typing, setTyping] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, typing]);

  const send = (text) => {
    if (!text.trim()) return;
    setMessages((m) => [...m, { from: 'user', text }]);
    setInput('');
    setTyping(true);
    setTimeout(() => {
      setMessages((m) => [...m, { from: 'ai', text: getReply(text) }]);
      setTyping(false);
    }, 900);
  };

  return (
    <>
      <motion.button
        whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.95 }}
        onClick={() => setOpen(!open)}
        className="chat-launcher"
        style={{
          position: 'fixed', bottom: 32, right: 32, zIndex: 999,
          width: 64, height: 64, borderRadius: '50%',
          background: '#1B6B3A', border: 'none', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 8px 30px rgba(27,107,58,0.4), 0 0 0 4px rgba(201,168,76,0.2)',
        }}
      >
        {open ? <X color="#FBF8F0" size={26} /> : (
          <CrescentMoon size={28} color="#FBF8F0" animate={false} />
        )}
        <svg style={{ position: 'absolute', inset: -4, transform: 'rotate(0deg)', animation: 'spin 8s linear infinite' }} viewBox="0 0 80 80">
          <circle cx="40" cy="40" r="38" fill="none" stroke="rgba(201,168,76,0.5)" strokeWidth="1.5" strokeDasharray="50 160" />
        </svg>
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.25, ease: [0.34, 1.56, 0.64, 1] }}
            className="chat-panel"
            style={{
              position: 'fixed', bottom: 110, right: 32, zIndex: 998,
              width: 380, maxWidth: 'calc(100vw - 40px)', height: 540,
              background: '#FBF8F0', borderTop: '3px solid #C9A84C',
              border: '1px solid rgba(201,168,76,0.25)',
              boxShadow: '0 20px 60px rgba(10,22,40,0.25)',
              display: 'flex', flexDirection: 'column',
            }}
          >
            <div style={{ background: '#0A1628', padding: '16px 18px', display: 'flex', alignItems: 'center', gap: 12 }}>
              <CrescentMoon size={22} color="#C9A84C" animate={false} />
              <div>
                <div style={{ fontFamily: 'Amiri', fontWeight: 700, color: '#FBF8F0', fontSize: 16, letterSpacing: '0.06em' }}>
                  Marhaba · <span className="arabic-text">مرحبا</span>
                </div>
                <div style={{ fontFamily: 'Inter', fontSize: 11, color: 'rgba(251,248,240,0.7)' }}>
                  {brandConfig.companyName} AI · متاح الآن
                </div>
              </div>
            </div>

            <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
              {messages.map((m, i) => (
                <div key={i} style={{
                  alignSelf: m.from === 'user' ? 'flex-end' : 'flex-start',
                  maxWidth: '85%',
                  background: m.from === 'user' ? '#0A1628' : '#FFFFFF',
                  color: m.from === 'user' ? '#FBF8F0' : '#0A1628',
                  borderLeft: m.from === 'ai' ? '3px solid #C9A84C' : 'none',
                  padding: '12px 14px',
                  fontFamily: 'Amiri, serif',
                  fontStyle: m.from === 'ai' ? 'italic' : 'normal',
                  fontSize: 14, lineHeight: 1.7,
                }}>
                  {m.text}
                </div>
              ))}
              {typing && (
                <div style={{ alignSelf: 'flex-start', display: 'flex', gap: 4, padding: 12 }}>
                  {[0, 1, 2].map((i) => (
                    <div key={i} style={{
                      width: 7, height: 7, borderRadius: '50%', background: '#C9A84C',
                      animation: `bounce 1.2s ease-in-out ${i * 0.15}s infinite`,
                    }} />
                  ))}
                </div>
              )}
              {messages.length === 1 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                  {CHIPS.map((c) => (
                    <button key={c} onClick={() => send(c)} style={{
                      background: '#FFFFFF', border: '1.5px solid rgba(201,168,76,0.3)',
                      fontFamily: 'Inter', fontWeight: 500, fontSize: 11,
                      color: '#0A1628', padding: '6px 12px', cursor: 'pointer',
                      letterSpacing: '0.05em', textTransform: 'uppercase',
                    }}>{c}</button>
                  ))}
                </div>
              )}
            </div>

            <div style={{ padding: 12, borderTop: '1px solid rgba(201,168,76,0.2)', display: 'flex', gap: 8 }}>
              <input
                value={input} onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && send(input)}
                placeholder="Type your message..."
                style={{
                  flex: 1, padding: '10px 14px', border: '1.5px solid rgba(201,168,76,0.3)',
                  background: '#FFFFFF', fontFamily: 'Inter', fontSize: 14,
                  color: '#0A1628', outline: 'none',
                }}
              />
              <button onClick={() => send(input)} style={{
                background: '#1B6B3A', border: 'none', padding: '10px 14px',
                cursor: 'pointer', display: 'flex', alignItems: 'center',
              }}>
                <Send color="#FBF8F0" size={16} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg);} to { transform: rotate(360deg);} }
        @keyframes bounce {
          0%, 60%, 100% { transform: translateY(0); opacity: 0.5; }
          30% { transform: translateY(-6px); opacity: 1; }
        }
        @media (max-width: 540px) {
          .chat-launcher { bottom: 20px !important; right: 20px !important; }
          .chat-panel { right: 12px !important; left: 12px !important; width: auto !important; bottom: 96px !important; }
        }
      `}</style>
    </>
  );
}
